// src/errors/ErrorPage.jsx
import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useRouteError } from "./ErrorRouteContext";

/**
 * ErrorPage component displays error information to the user and provides actions to recover.
 *
 * - If no error is present, redirects to the home page.
 * - Shows error title, status, message, and optional details.
 * - Provides "Try again" (if retry handler is present), "Go back", and "Home" actions.
 * - Clears error state on unmount or when navigating away.
 *
 * @component
 * @returns {JSX.Element|null} The rendered error page or null if redirecting.
 */
export default function ErrorPage() {
  const { error, clearError } = useRouteError();
  const navigate = useNavigate();

  // Clear page state when unmounting
  // useEffect(() => () => clearError(), [clearError]); //don't auto clear, in Dev environment it clears error too soon
  // console.log(error);
  // if (!error) {
  //   // Direct visit or after refresh: bounce home (or show a generic message)
  //   navigate("/", { replace: true });
  //   return null;
  // }

  if (!error) {
    // Instead of auto-redirect (which can loop), show a benign fallback.
    return (
      <main className="mx-auto max-w-xl p-6">
        <h1 className="text-2xl font-semibold mb-2">No error to show</h1>
        <p className="mb-4">You can safely go back home.</p>
        <button
          className="px-4 py-2 rounded border"
          onClick={() => navigate("/")}
        >
          Home
        </button>
      </main>
    );
  }

  const { title, message, status, detail, backTo, onRetry } = error;

  return (
    <main className="mx-auto max-w-xl p-6">
      <h1 className="text-2xl font-semibold mb-2">{title}</h1>
      {status != null && <p className="opacity-70 mb-2">Status: {status}</p>}
      <p className="mb-4">{message}</p>

      {detail && (
        <pre className="bg-gray-100 p-3 rounded overflow-auto text-sm mb-6">
          {typeof detail === "string"
            ? detail
            : JSON.stringify(detail, null, 2)}
        </pre>
      )}

      <div className="flex gap-2">
        {onRetry && typeof onRetry === "function" && (
          <button
            className="px-4 py-2 rounded bg-black text-white"
            onClick={async () => {
              try {
                await onRetry();
                clearError();
              } finally {
                /* stay on page if retry throws */
              }
            }}
          >
            Try again
          </button>
        )}
        {onRetry && typeof onRetry !== "function" && (
          <button
            className="px-4 py-2 rounded bg-black text-white"
            onClick={async () => {
              clearError();
              navigate(onRetry || "/");
            }}
          >
            Try again
          </button>
        )}
        {backTo && (
          <button
            className="px-4 py-2 rounded border"
            onClick={() => {
              clearError();
              navigate(backTo || -1);
            }}
          >
            Go back
          </button>
        )}
        <button
          className="px-4 py-2 rounded border"
          onClick={() => {
            clearError();
            navigate("/");
          }}
        >
          Home
        </button>
      </div>
    </main>
  );
}
